<template>
  <div>
    <search-page :key="searchPageKey" @searchEvent="searchEvent" :cacheIsNeeded="true"></search-page>
    <div v-if="loading">
      <b-spinner small variant="primary" label="Loading..." class="mr-1"></b-spinner
      >Palaukite, ruošiamas sąrašas....
    </div>
    <list-body
      v-else
      :documentList="documentList"
      :currentPage="currentPage"
      :resultPerPage="resultPerPage"
      @searchEvent="handleSearchEvent"
      class="mt-4"
    ></list-body>

    <div class="ln-bt-g mt-3 mb-3"></div>

    <paginate
      v-if="ready"
      v-model="currentPage"
      :page-count="pageCount"
      :page-range="2"
      :margin-pages="1"
      :click-handler="changePage"
      :first-last-button="true"
      prev-text="&lsaquo;"
      next-text="&rsaquo;"
      first-button-text="&laquo;"
      last-button-text="&raquo;"
      :page-class="'page-item'"
      :page-link-class="'page-link'"
      :prev-link-class="'page-link'"
      :next-link-class="'page-link'"
      :container-class="'pagination'"
    ></paginate>
  </div>
</template>

<script>
import { GetDocumentList, GetPublicationsList } from "../Assets/repository";
import SearchUrlParams from "../_Helpers/search-url-params";
import SearchPage from "./search-page";
import ListBody from "./Components/list-body";

export default {
  name: "list-page",
  components: { SearchPage, ListBody },
  created() {
    // Nustatome cache galiojimo laiką (24 valandos)
    const CACHE_EXPIRY_HOURS = 0.5;
    // Bandome užkrauti duomenis iš localStorage arba Vuex store
    const cachedData =
      this.loadFromLocalStorage() ||
      (this.$store.state.searchCacheReady ? this.$store.state.searchCache : null);

    // Tikriname ar cache dar galioja
    const isCacheValid =
      cachedData &&
      cachedData.timestamp &&
      Date.now() - cachedData.timestamp < CACHE_EXPIRY_HOURS * 60 * 60 * 1000;

    // Patikriname ar cached query parametrai sutampa su dabartiniais route query
    const cachedQueryParams = cachedData ? cachedData.queryParams : null;
    const currentQueryParams = SearchUrlParams.encode(this.$route.query);

    if (cachedData && currentQueryParams && cachedQueryParams !== currentQueryParams) {
      cachedData.queryParams = currentQueryParams;
    }

    if (cachedData && isCacheValid && cachedData.value && this.$route.params.loadFromLocalStorage) {
      // console.log("Using cached search results...");
      this.$router.push("/list" + cachedData.queryParams);
      this.documentList = cachedData.value;
      this.currentPage = cachedData.currentPage;
      this.resultPerPage = cachedData.resultPerPage;
      this.ready = true;

      // Atnaujiname store su naujausiais duomenimis
      this.$store.dispatch("setSearchCache", cachedData);
    } else {
      // console.log("No valid cache found, performing new search...");
      this.clearLocalStorage();
      this.httpGet();
    }
  },
  methods: {
    changePage(n) {
      this.currentPage = n;
      this.httpGet();
    },
    httpGet() {
      this.loading = true;
      let that = this;
      GetDocumentList(this.queryParams)
        .then((res) => {
          that.documentList = res.data;
          that.ready = true;
          that.loading = false;

          const cacheData = {
            currentPage: that.currentPage,
            resultPerPage: that.resultPerPage,
            queryParams: that.queryParams,
            value: that.documentList,
            timestamp: Date.now(), // Pridedame timestamp, kad žinotume kada buvo atnaujintas cache
          };

          that.$store.dispatch("setSearchCache", cacheData);
          this.saveToLocalStorage(cacheData);

          if (this.isPublicationsDisplayInSearchResultsEnabled) {
            this.loadPublicationsForDocuments();
          }
        })
        .catch(() => {
          this.$router.push("404");
        });
    },
    loadPublicationsForDocuments() {
      let that = this;
      that.documentList.items.forEach((item) => {
        item.publicationsLoaded = false;
      });

      let docIdList = that.documentList.items.map((item) => item.id);
      if (docIdList.length > 0) {
        GetPublicationsList(docIdList.join(","))
          .then((res) => {
            that.documentList.items.forEach((item) => {
              item.publications = res.data.filter(
                (publication) => publication.c_id === item.id
              );
              item.publicationsLoaded = true;
            });
            that.documentList = { ...that.documentList };

            const updatedCache = {
              currentPage: that.currentPage,
              resultPerPage: that.resultPerPage,
              queryParams: that.queryParams,
              value: that.documentList,
              timestamp: Date.now(), // Atnaujiname timestamp, kad žinotume kada buvo atnaujintas cache
            };

            that.$store.dispatch("setSearchCache", updatedCache);
            this.saveToLocalStorage(updatedCache);
          })
          .catch((ex) => {
            console.error(ex);
          });
      }
    },
    searchEvent() {
      this.currentPage = 1;
      this.httpGet();
    },
    handleSearchEvent(query) {
      this.$router.push({ name: 'list', query });
      this.searchPageKey += 1;
      this.searchEvent();
    },
    saveToLocalStorage(data) {
      localStorage.setItem(
        "queryParams",
        JSON.stringify(data || this.$store.state.searchCache)
      );
    },
    loadFromLocalStorage() {
      const data = localStorage.getItem("queryParams");
      return data ? JSON.parse(data) : null;
    },
    clearLocalStorage() {
      localStorage.removeItem("queryParams");
    },
  },
  computed: {
    params() {
      return SearchUrlParams.decode(this.$route.query);
    },
    hasParams() {
      return Object.values(this.params).some((param) => param != null);
    },
    queryParams() {
      let params = this.params;
      params.puslapis = this.currentPage;
      params.kiekis = this.resultPerPage;
      return SearchUrlParams.encode(params);
    },
    pageCount() {
      return Math.ceil(this.documentList.count / this.resultPerPage);
    },
    isPublicationsDisplayInSearchResultsEnabled() {
      return process.env.VUE_APP_SEARCH_DISPLAY_PUBLICATIONS === "true";
    },
  },
  data() {
    return {
      documentList: undefined,
      ready: false,
      loading: false,
      currentPage: 1,
      resultPerPage: 10,
      searchPageKey: 0,
    };
  },
};
</script>

<style lang="css" scoped>
.pointer {
  cursor: pointer;
}
</style>
